import { AccountTree } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React from "react";
import ColorService from "../../../services/ColorService";
import IconService from "../../../services/IconService";
import UtilService from "../../../services/UtilService";
import { getPuzzleTypeById } from "../PuzzleTypeConstants";

const StyledCard = styled(Card)(({ theme }) => ({
    minHeight: 200,
    width: '500px',
    maxWidth: '100%',
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s ease-in-out",
    cursor: "pointer",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[8],
    },
}));

const WordCountChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
}));

export default function PuzzleCard({ puzzle, onClick }) {
    // Calculate word count from the wordsInUse string
    const wordCount = puzzle.wordsInUse ? puzzle.wordsInUse.split('\n').filter(word => word.trim()).length : 0;
    const wordSearchRows = Number(puzzle.gridRows ?? puzzle.grid_rows);
    const wordSearchCols = Number(puzzle.gridCols ?? puzzle.grid_cols);
    const wordSearchLabel =
        puzzle.type === "WORD_SEARCH" &&
        Number.isFinite(wordSearchRows) &&
        wordSearchRows > 0 &&
        Number.isFinite(wordSearchCols) &&
        wordSearchCols > 0
            ? `${wordSearchRows}×${wordSearchCols}`
            : null;

    const { title, name, createdAt, updatedAt, state, publishedAt, topic, difficultyLevel } = puzzle;

    // Check if puzzle has wordNetwork and get node count
    const getWordNetworkInfo = () => {
        const wordNetwork = puzzle.wordNetwork || puzzle.template?.wordNetwork;
        if (wordNetwork && wordNetwork.nodes && Array.isArray(wordNetwork.nodes)) {
            return {
                hasNetwork: true,
                nodeCount: wordNetwork.nodes.length
            };
        }
        return {
            hasNetwork: false,
            nodeCount: 0
        };
    };

    const wordNetworkInfo = getWordNetworkInfo();
    const tagsList = (puzzle?.tags ?? puzzle?.template?.tags ?? []).filter(Boolean);

    return (
        <StyledCard elevation={2} onClick={() => onClick && onClick(puzzle)}>
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '3.5em',
                            lineHeight: '1.75em'
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                    <WordCountChip
                        label={`${wordCount} words`}
                        size="small"
                        variant="outlined"
                    />

                    <Tooltip title={wordNetworkInfo.hasNetwork ? `Word Network (${wordNetworkInfo.nodeCount} nodes)` : "No Word Network"}>
                        <Chip
                            icon={<AccountTree />}
                            label={wordNetworkInfo.hasNetwork ? wordNetworkInfo.nodeCount : "No Network"}
                            size="small"
                            variant="outlined"
                            sx={{
                                color: wordNetworkInfo.hasNetwork ? 'inherit' : 'red',
                                borderColor: wordNetworkInfo.hasNetwork ? 'inherit' : 'red',
                                '& .MuiChip-icon': {
                                    color: wordNetworkInfo.hasNetwork ? 'inherit' : 'red'
                                }
                            }}
                        />
                    </Tooltip>
                </Box>

                {tagsList.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                        {tagsList.map((tag, index) => {
                            const tagStr = typeof tag === 'string' ? tag : (tag?.tag ?? tag?.name ?? String(tag));
                            if (!tagStr) return null;
                            return (
                                <Chip
                                    key={index}
                                    label={tagStr}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ fontSize: "0.75rem" }}
                                />
                            );
                        })}
                    </Box>
                )}

                {(state || difficultyLevel || puzzle.free !== undefined || puzzle.type) && (
                    <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {puzzle.type && (
                            <Chip
                                label={getPuzzleTypeById(puzzle.type)?.name || puzzle.type}
                                icon={<span>{IconService.getPuzzleTypeIcon(puzzle.type)}</span>}
                                size="small"
                                sx={{
                                    backgroundColor: "#2196f3",
                                    color: "white"
                                }}
                            />
                        )}
                        {wordSearchLabel != null && (
                            <Chip
                                label={wordSearchLabel}
                                size="small"
                                sx={{
                                    backgroundColor: "#546e7a",
                                    color: "white"
                                }}
                            />
                        )}
                        {state && (
                            <Chip
                                label={state?.toLowerCase().replace("_", " ")}
                                size="small"
                                sx={{
                                    backgroundColor: ColorService.getStateColor(state),
                                    color: "white",
                                    textTransform: "capitalize"
                                }}
                            />
                        )}
                        {difficultyLevel && (
                            <Chip
                                label={difficultyLevel?.toLowerCase()}
                                size="small"
                                sx={{
                                    backgroundColor: ColorService.getDifficultyColor(difficultyLevel),
                                    color: "white",
                                    textTransform: "capitalize"
                                }}
                            />
                        )}
                        {(puzzle.free === true || puzzle.free === false || puzzle.free === null) && (
                            <Chip
                                label={puzzle.free === true ? "FREE" : "PAID"}
                                size="small"
                                sx={{
                                    backgroundColor: ColorService.getPricingColor(puzzle.free),
                                    color: "white",
                                    textTransform: "capitalize"
                                }}
                            />
                        )}
                    </Box>
                )}

                <Box sx={{ mt: "auto", pt: 2 }}>
                    {(puzzle.creatorId || puzzle.creator || puzzle.cloudUser) && (() => {
                        const getCreatorDisplay = () => {
                            const creator = puzzle.creator || puzzle.cloudUser;
                            if (creator) {
                                const firstName = creator.firstName || '';
                                const lastName = creator.lastName || '';
                                const email = creator.email || '';
                                const nameParts = [firstName, lastName].filter(Boolean);
                                if (nameParts.length > 0 && email) {
                                    return `${nameParts.join(' ')} (${email})`;
                                } else if (nameParts.length > 0) {
                                    return nameParts.join(' ');
                                } else if (email) {
                                    return email;
                                }
                            }
                            return puzzle.creatorName || puzzle.creatorId || 'Unknown';
                        };
                        return (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontStyle: "italic",
                                    fontSize: "0.875rem",
                                    mb: 0.5
                                }}
                            >
                                Creator: {getCreatorDisplay()}
                            </Typography>
                        );
                    })()}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            fontStyle: "italic",
                            fontSize: "0.875rem"
                        }}
                    >
                        Created: {(() => {
                            if (!createdAt) return "Unknown";
                            const timestampMs = typeof createdAt === "number" ? createdAt : new Date(createdAt).getTime();
                            const durationMs = Date.now() - timestampMs;
                            const formatted = UtilService.formatHumanizeDetailed(durationMs);
                            return formatted || UtilService.formatDateHM(createdAt);
                        })()}
                    </Typography>
                    {updatedAt && updatedAt !== 0 && updatedAt !== "0" && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontStyle: "italic",
                                fontSize: "0.875rem",
                                mt: 0.5
                            }}
                        >
                            Updated: {(() => {
                                const timestampMs = typeof updatedAt === "number" ? updatedAt : new Date(updatedAt).getTime();
                                const durationMs = Date.now() - timestampMs;
                                const formatted = UtilService.formatHumanizeDetailed(durationMs);
                                return formatted || UtilService.formatDateHM(updatedAt);
                            })()}
                        </Typography>
                    )}
                    {state === "PUBLISHED" && publishedAt && publishedAt !== 0 && publishedAt !== "0" && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontStyle: "italic",
                                fontSize: "0.875rem",
                                mt: 0.5
                            }}
                        >
                            Published: {(() => {
                                const timestampMs = typeof publishedAt === "number" ? publishedAt : new Date(publishedAt).getTime();
                                const durationMs = Date.now() - timestampMs;
                                const formatted = UtilService.formatHumanizeDetailed(durationMs);
                                return formatted || UtilService.formatDateHM(publishedAt);
                            })()}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </StyledCard>
    );
}

